import { enqueueActivityEvent, isActivityQueueEnabled } from 'services/activityTracking/activityEventQueue';
import { getProducerSessionId, nextEventId, nextProducerSequence } from 'services/activityTracking/session';
import {
    ACTIVITY_EVENT_TYPE,
    ACTIVITY_FIELD_REVIEW_RESULT,
    ACTIVITY_OPERATOR_HEARTBEAT_MS,
    ACTIVITY_OPERATOR_STATE,
} from 'utils/activityTracking/constants';
import { buildActivityEvent } from 'utils/activityTracking/envelope';
import { toSchemaFieldKey } from 'utils/activityTracking/fieldKey';

let currentState = ACTIVITY_OPERATOR_STATE.UNKNOWN;
let heartbeatTimer = null;
let sessionEnded = false;

function emit(intent) {
    if (!isActivityQueueEnabled()) return;
    enqueueActivityEvent(
        buildActivityEvent({
            eventId: nextEventId(),
            occurredAt: new Date().toISOString(),
            producerSessionId: getProducerSessionId(),
            producerSequence: nextProducerSequence(),
            ...intent,
        })
    );
}

export function transitionOperatorState(state, stateReason = null) {
    try {
        if (!state || sessionEnded || state === currentState) return;
        const previousState = currentState;
        currentState = state;
        if (state === ACTIVITY_OPERATOR_STATE.OFF_DUTY) sessionEnded = true;
        emit({ eventType: ACTIVITY_EVENT_TYPE.OPERATOR_STATE_CHANGED, state, previousState, stateReason });
    } catch (error) {
        console.warn('activityTracking: operator state change failed.', error);
    }
}

export function snapshotOperatorState(state, snapshotReason) {
    try {
        if (!state) return;
        currentState = state;
        sessionEnded = false;
        emit({ eventType: ACTIVITY_EVENT_TYPE.OPERATOR_STATE_SNAPSHOT, state, snapshotReason });
    } catch (error) {
        console.warn('activityTracking: operator state snapshot failed.', error);
    }
}

export function emitOperatorHeartbeat() {
    try {
        if (sessionEnded) return;
        emit({ eventType: ACTIVITY_EVENT_TYPE.OPERATOR_HEARTBEAT, state: currentState });
    } catch (error) {
        console.warn('activityTracking: operator heartbeat failed.', error);
    }
}

export function startOperatorHeartbeat() {
    // A zero interval must disable the heartbeat, not schedule it continuously.
    if (!ACTIVITY_OPERATOR_HEARTBEAT_MS || ACTIVITY_OPERATOR_HEARTBEAT_MS <= 0) return;
    if (heartbeatTimer !== null) return;
    heartbeatTimer = setInterval(emitOperatorHeartbeat, ACTIVITY_OPERATOR_HEARTBEAT_MS);
}

export function stopOperatorHeartbeat() {
    if (heartbeatTimer !== null) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
}

export function recordFieldReviewCompleted({ transactionId, fieldKey, fieldType, reviewResult, context } = {}) {
    try {
        const guardedKey = toSchemaFieldKey(fieldKey);
        const workObjectId = transactionId === null || transactionId === undefined ? null : String(transactionId);
        if (!guardedKey || !workObjectId || !Object.values(ACTIVITY_FIELD_REVIEW_RESULT).includes(reviewResult)) return;

        emit({
            eventType: ACTIVITY_EVENT_TYPE.FIELD_REVIEW_COMPLETED,
            workObjectId,
            targetId: guardedKey,
            fieldKey: guardedKey,
            fieldType: fieldType ?? null,
            reviewResult,
            locationId: context?.locationId ?? null,
            gateId: context?.gateId ?? null,
            deskId: context?.deskId ?? null,
        });
    } catch (error) {
        console.warn('activityTracking: field review event failed.', error);
    }
}

export function resetOperatorStateTracker() {
    stopOperatorHeartbeat();
    currentState = ACTIVITY_OPERATOR_STATE.UNKNOWN;
    sessionEnded = false;
}
